import React, { useEffect, useState } from 'react';
import { Sidebar } from '../components/layout/Sidebar';
import { TopTabs, type TabKey } from '../components/layout/TopTabs';
import { ExamListPage } from './exams/ExamListPage';
import { ProfileDrawer } from '../components/profile/ProfileDrawer';
import { SupportChatPanel } from '../components/support/SupportChatPanel';
import { useAuthStore } from '../stores/authStore';
import { useAccountStore } from '../stores/accountStore';
import { supportApi } from '../services/supportApi';

/** 登录后的主框架：左 Sidebar + 顶部 Tab + 内容区（考试列表）。 */
const HomeLayout: React.FC = () => {
  const { accessToken, getCurrentUser } = useAuthStore();
  const { account, fetchAccount, canSeeTab } = useAccountStore();

  const [activeTab, setActiveTab] = useState<TabKey>('practice');
  const [profileOpen, setProfileOpen] = useState(false);

  // 客服窗口状态
  const [supportConversationId, setSupportConversationId] = useState<string | null>(null);
  const [supportUnread, setSupportUnread] = useState(0);
  const [contacting, setContacting] = useState(false);
  // 仅当用户曾主动给客服发过消息，才启动未读轮询（持久化，跨会话保留）
  const [hasContacted, setHasContacted] = useState<boolean>(
    () => localStorage.getItem('support_contacted') === '1'
  );

  // 初始加载：用户信息 + 账户信息
  useEffect(() => {
    getCurrentUser();
    fetchAccount();
  }, []);

  // 账户加载后，若当前 tab 不可见则回落到 practice
  useEffect(() => {
    if (account && !canSeeTab(activeTab)) setActiveTab('practice');
  }, [account]);

  // 客服未读轮询：仅在用户曾发过消息（hasContacted）后才启动
  useEffect(() => {
    if (!accessToken || !hasContacted) return;
    let mounted = true;
    const refresh = () => {
      supportApi.getUnread().then((n) => { if (mounted) setSupportUnread(n); }).catch(() => {});
    };
    refresh();
    const timer = setInterval(refresh, 30000);
    return () => { mounted = false; clearInterval(timer); };
  }, [accessToken, hasContacted]);

  // 用户首次发消息 → 置位并持久化，启动轮询
  const markContacted = () => {
    if (hasContacted) return;
    localStorage.setItem('support_contacted', '1');
    setHasContacted(true);
  };

  const handleContact = async () => {
    if (supportConversationId) return; // 已打开
    if (contacting) return;
    setContacting(true);
    try {
      const result = await supportApi.openConversation();
      setSupportConversationId(result.conversation.id);
      supportApi.markRead().catch(() => {});
      setSupportUnread(0);
    } catch {
      /* 忽略 */
    } finally {
      setContacting(false);
    }
  };

  const handleSupportClose = () => {
    setSupportConversationId(null);
    supportApi.markRead().catch(() => {});
    setSupportUnread(0);
  };

  return (
    <div className="h-screen flex bg-primary-50/30">
      <Sidebar
        unread={supportUnread}
        onContact={handleContact}
        onOpenProfile={() => setProfileOpen(true)}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <TopTabs active={activeTab} onChange={setActiveTab} />
        <main className="flex-1 overflow-y-auto">
          <ExamListPage tab={activeTab} />
        </main>
      </div>

      <ProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />

      {supportConversationId && (
        <SupportChatPanel
          conversationId={supportConversationId}
          onClose={handleSupportClose}
          onUserSent={markContacted}
        />
      )}
    </div>
  );
};

export default HomeLayout;
